/* eslint-disable react-hooks/exhaustive-deps */
import { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import axios from 'axios';
import * as XLSX from 'xlsx';
import {
  Sun, X, AlertTriangle, Calendar, TrendingUp, Bell, Phone, MessageCircle, Mail,
  UserPlus, Download, Loader2, RefreshCw, ChevronRight, Package, Users, Clock, DownloadCloud,
  Camera, Trophy, Wallet
} from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from '../context/AuthContext';
import { useYetki } from '../context/YetkiContext';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const SHOWN_KEY = 'gunaydin_last_shown';

const todayStr = () => new Date().toISOString().slice(0, 10);

// Cookie yedeği — appVersion sürüm güncellemesi localStorage.clear() yapınca
// "bugün için kapat" bilgisi kaybolmasın (gün içinde tekrar açılmasın).
const _ckGet = (n) => {
  try { const m = document.cookie.match(new RegExp('(?:^|; )' + n + '=([^;]+)')); return m ? decodeURIComponent(m[1]) : null; }
  catch { return null; }
};
const _ckSet = (n, v, days = 2) => {
  try { const d = new Date(); d.setTime(d.getTime() + days * 864e5);
    document.cookie = `${n}=${encodeURIComponent(v)}; expires=${d.toUTCString()}; path=/; SameSite=Lax`; }
  catch { /* noop */ }
};
const gunaydinSonGosterim = () => {
  try { return localStorage.getItem(SHOWN_KEY) || _ckGet(SHOWN_KEY); } catch { return _ckGet(SHOWN_KEY); }
};
const gunaydinIsaretle = (v) => {
  try { localStorage.setItem(SHOWN_KEY, v); } catch { /* noop */ }
  _ckSet(SHOWN_KEY, v);
};

const fmtMoney = (v) => (Number(v) || 0).toLocaleString('tr-TR', {
  minimumFractionDigits: 0, maximumFractionDigits: 0
});

const fmtPhoneWA = (tel) => {
  if (!tel) return '';
  const d = String(tel).replace(/\D/g, '');
  if (!d) return '';
  if (d.startsWith('90')) return d;
  if (d.startsWith('0')) return '90' + d.substring(1);
  if (d.length === 10) return '90' + d;
  return d;
};

const GunaydinDashboard = () => {
  const { user } = useAuth();
  const role = user?.role;
  // KİŞİ BAZLI (2026-09-30): rol değil, Kullanıcı Yönetimi → Yetkiler'deki "Günaydın" tiki.
  // Superadmin her zaman; backend aynı kuralla kapı tutuyor (services/kisi_bazli_yetki).
  const { sayfaGorebilir } = useYetki();
  const enabled = role === 'superadmin' || sayfaGorebilir('gunaydin') === true;

  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState(null);
  const [aktifSekme, setAktifSekme] = useState('vadesi_gecmis');
  const [selectedIds, setSelectedIds] = useState([]);
  const [gorevModal, setGorevModal] = useState(null);
  const [personeller, setPersoneller] = useState([]);
  const [syncLoading, setSyncLoading] = useState(false);
  const [atadigimGorevler, setAtadigimGorevler] = useState({ tasks: [], ozet: {} });
  const [gorevDurumModal, setGorevDurumModal] = useState(null);

  // Otomatik açılış — günde 1 kez
  useEffect(() => {
    if (!enabled) return;
    try {
      const last = gunaydinSonGosterim();   // localStorage + cookie yedeği
      if (last !== todayStr()) {
        setTimeout(() => setOpen(true), 1500); // Login sonrası hafif gecikme
      }
    } catch { /* noop */ }
  }, [enabled]);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const res = await axios.get(`${API}/panel/gunaydin-dashboard`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.data.success) {
        setData(res.data);
      } else {
        toast.error(res.data.error || 'Veri yüklenemedi');
      }
    } catch (e) {
      toast.error(e.response?.data?.error || 'Sunucu hatası');
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchPersoneller = useCallback(async () => {
    try {
      const token = localStorage.getItem('token');
      const res = await axios.get(`${API}/panel/gunaydin-personeller`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.data.success) {
        setPersoneller(res.data.personel || []);
      }
    } catch {
      // sessiz
    }
  }, []);

  const fetchAtadigimGorevler = useCallback(async () => {
    try {
      const token = localStorage.getItem('token');
      const res = await axios.get(`${API}/panel/gunaydin-atadigim-gorevler`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.data.success) {
        setAtadigimGorevler({ tasks: res.data.tasks || [], ozet: res.data.ozet || {} });
      }
    } catch { /* noop */ }
  }, []);

  const handleCariSync = async () => {
    if (!window.confirm('Cari hesaplar Logo\'dan güncellensin mi? Bu işlem 1-3 dakika sürebilir.')) return;
    setSyncLoading(true);
    try {
      const token = localStorage.getItem('token');
      const res = await axios.post(`${API}/panel/gunaydin-cari-sync`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.data.success) {
        const s = res.data.sonuc || {};
        toast.success(`Sync tamam: ${s.toplam_cari_guncellenen || 0} cari, ${s.toplam_hareket_guncellenen || 0} hareket güncellendi`);
        setData(null);
        fetchData();
      } else {
        toast.error(res.data.error || 'Sync başarısız');
      }
    } catch (e) {
      toast.error(e.response?.data?.error || 'Sync hatası');
    } finally {
      setSyncLoading(false);
    }
  };

  useEffect(() => {
    if (open && !data) fetchData();
    if (open && personeller.length === 0) fetchPersoneller();
    // Atadığım görevler badge'i için açılır açılmaz fetch et
    if (open) fetchAtadigimGorevler();
  }, [open, data, fetchData, personeller.length, fetchPersoneller, fetchAtadigimGorevler]);

  const markShownForToday = () => {
    gunaydinIsaretle(todayStr());  // localStorage + cookie → gün içinde tekrar açılmaz
  };

  const handleClose = () => {
    markShownForToday();
    setOpen(false);
  };

  const handleReaçOnce = () => {
    // "Tekrar göster" seçeneği — localStorage + cookie temizle
    try { localStorage.removeItem(SHOWN_KEY); } catch { /* noop */ }
    _ckSet(SHOWN_KEY, '', -1);  // cookie sil
    toast.info('Yarın tekrar açılacak');
  };

  const toggleSelect = (id) => {
    setSelectedIds(prev => prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]);
  };

  const vadeList = data?.vadesi_gecmis?.liste || [];
  const haftaList = data?.bu_hafta_vade?.liste || [];
  const ozet = data?.gunun_ozeti || {};
  const yeniMusteriler = data?.yeni_musteriler || [];
  const uyarilar = data?.uyarilar || {};

  const seciliVadeRows = useMemo(() =>
    vadeList.filter(r => selectedIds.includes(r.user_id)),
    [vadeList, selectedIds]
  );

  // ===== AKSIYON: WhatsApp =====
  const wpMsg = (r) => encodeURIComponent(
    `Merhaba ${r.musteri_ad},\n\n${fmtMoney(r.tutar)} TL tutarında vadesi geçmiş bakiyeniz hakkında görüşmek istiyoruz. (Gecikme: ${r.gecikme_gun} gün, ${r.fatura_sayisi} fatura)\n\nİyi günler.`
  );
  const waUrl = (r) => {
    const tel = fmtPhoneWA(r.telefon);
    if (!tel) return null;
    return `https://wa.me/${tel}?text=${wpMsg(r)}`;
  };

  const handleWhatsappToplu = () => {
    if (seciliVadeRows.length === 0) { toast.info('Önce satır seçin'); return; }
    // Toplu link — ilk satır için aç, kullanıcı sonrakiler için teker teker butonu kullansın
    const first = seciliVadeRows[0];
    const url = waUrl(first);
    if (url) {
      window.open(url, '_blank');
      if (seciliVadeRows.length > 1) {
        toast.info(`${seciliVadeRows.length} seçili. Her satır için ayrı WhatsApp butonunu kullanın.`);
      }
    } else {
      toast.error('İlk satırda telefon yok');
    }
  };

  // ===== AKSIYON: Email mailto =====
  const emailBody = (r) => encodeURIComponent(
    `Sayın ${r.musteri_ad},\n\nŞirketimizdeki ${fmtMoney(r.tutar)} TL tutarındaki ${r.fatura_sayisi} adet vadesi geçmiş faturanızın ödenmesini rica ederiz. (Gecikme: ${r.gecikme_gun} gün)\n\nSaygılarımızla.`
  );
  const mailtoUrl = (r) => r.email ? `mailto:${r.email}?subject=${encodeURIComponent('Vadesi Geçmiş Bakiye Hakkında')}&body=${emailBody(r)}` : null;

  // ===== AKSIYON: Excel =====
  const handleExcel = () => {
    const wb = XLSX.utils.book_new();

    // Sheet 1: Vadesi Geçmiş
    const s1 = XLSX.utils.aoa_to_sheet([
      ['Vadesi Geçmiş Alacaklar - ' + new Date().toLocaleString('tr-TR')],
      [`Günü geçen: ${fmtMoney(data?.vadesi_gecmis?.toplam_tutar)} TL | Toplam bakiye: ${fmtMoney(data?.vadesi_gecmis?.toplam_bakiye)} TL | Kritik (30+gün): ${fmtMoney(data?.vadesi_gecmis?.kritik_tutar)} TL | ${data?.vadesi_gecmis?.musteri_sayisi} müşteri`],
      [],
      ['#', 'Firma', 'Müşteri No', 'Müşteri Ünvanı', 'Cari Kodu', 'Telefon', 'E-posta', 'Gecikme (Gün)', 'Fatura Adedi', 'Günü Geçen (TL)', 'Toplam Bakiye (TL)', 'Risk'],
      ...vadeList.map((r, i) => [
        i + 1, r.firma, r.musteri_no, r.musteri_ad, r.cari_kodu,
        r.telefon, r.email, r.gecikme_gun, r.fatura_sayisi, r.tutar, r.bakiye ?? '', r.risk_seviyesi
      ])
    ]);
    s1['!cols'] = [{ wch: 4 }, { wch: 10 }, { wch: 10 }, { wch: 35 }, { wch: 15 }, { wch: 15 }, { wch: 25 }, { wch: 12 }, { wch: 8 }, { wch: 14 }, { wch: 16 }, { wch: 10 }];
    XLSX.utils.book_append_sheet(wb, s1, 'Vadesi Gecmis');

    // Sheet 2: Bu Hafta Plan
    const s2 = XLSX.utils.aoa_to_sheet([
      ['Bu Hafta Tahsilat Planı'],
      [`Toplam: ${fmtMoney(data?.bu_hafta_vade?.toplam_tutar)} TL | ${data?.bu_hafta_vade?.kayit_sayisi} fatura`],
      [],
      ['#', 'Firma', 'Müşteri Ünvanı', 'Cari Kodu', 'Fatura No', 'Vade Tarihi', 'Gün Farkı', 'Tutar (TL)', 'Telefon'],
      ...haftaList.map((r, i) => [
        i + 1, r.firma, r.musteri_ad, r.cari_kodu, r.fatura_no,
        new Date(r.vade_tarihi).toLocaleDateString('tr-TR'),
        r.gun_farki, r.tutar, r.telefon
      ])
    ]);
    s2['!cols'] = [{ wch: 4 }, { wch: 10 }, { wch: 35 }, { wch: 15 }, { wch: 15 }, { wch: 14 }, { wch: 10 }, { wch: 14 }, { wch: 15 }];
    XLSX.utils.book_append_sheet(wb, s2, 'Bu Hafta Plan');

    // Sheet 3: Özet
    const s3 = XLSX.utils.aoa_to_sheet([
      ['Günün Özeti'],
      [],
      ['Metrik', 'Değer'],
      ['Bekleyen Sipariş', ozet.bekleyen_siparis || 0],
      ['Aday Müşteri (Eşleşme Bekliyor)', ozet.aday_musteri_sayisi || 0],
      ['Yeni Müşteri', ozet.yeni_musteri_sayisi || 0],
    ]);
    XLSX.utils.book_append_sheet(wb, s3, 'Ozet');

    XLSX.writeFile(wb, `gunaydin_${todayStr()}.xlsx`);
    toast.success('Excel dosyası indirildi');
  };

  // ===== AKSIYON: Görev ata =====
  const handleGorevAta = async (payload) => {
    try {
      const token = localStorage.getItem('token');
      const res = await axios.post(`${API}/panel/gunaydin-gorev-ata`, payload, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.data.success) {
        toast.success(res.data.message || 'Görev oluşturuldu');
        setGorevModal(null);
        // Badge sayısı güncellensin
        fetchAtadigimGorevler();
      } else {
        toast.error(res.data.error || 'Görev oluşturulamadı');
      }
    } catch (e) {
      toast.error(e.response?.data?.error || 'Sunucu hatası');
    }
  };

  // Görev durumunu güncelle (atayan kişi)
  const handleGorevDurum = async (taskId, durum, sonuc) => {
    try {
      const token = localStorage.getItem('token');
      const res = await axios.post(`${API}/panel/gunaydin-gorev-durum-guncelle`, {
        task_id: taskId, durum, sonuc
      }, { headers: { Authorization: `Bearer ${token}` } });
      if (res.data.success) {
        toast.success(res.data.message);
        setGorevDurumModal(null);
        fetchAtadigimGorevler();
      } else {
        toast.error(res.data.error);
      }
    } catch (e) {
      toast.error(e.response?.data?.error || 'Sunucu hatası');
    }
  };

  // 🟡 Sürüklenebilir floating ikon — konum localStorage'da saklanır
  const [btnPos, setBtnPos] = useState(() => {
    try {
      const s = JSON.parse(localStorage.getItem('gunaydin_btn_pos'));
      if (s && typeof s.x === 'number' && typeof s.y === 'number') return s;
    } catch (_) { /* yok say */ }
    return null; // null → varsayılan sağ-alt (CSS bottom-5 right-5)
  });
  const dragRef = useRef({ dragging: false, moved: false });

  const onBtnPointerDown = (e) => {
    const rect = e.currentTarget.getBoundingClientRect();
    dragRef.current = {
      dragging: true,
      moved: false,
      startX: e.clientX,
      startY: e.clientY,
      offsetX: e.clientX - rect.left,
      offsetY: e.clientY - rect.top,
      lastPos: null,
    };
    try { e.currentTarget.setPointerCapture(e.pointerId); } catch (_) {}
  };
  const onBtnPointerMove = (e) => {
    const d = dragRef.current;
    if (!d.dragging) return;
    if (Math.abs(e.clientX - d.startX) > 4 || Math.abs(e.clientY - d.startY) > 4) d.moved = true;
    if (!d.moved) return;
    const SIZE = 56;
    const x = Math.max(4, Math.min(window.innerWidth - SIZE - 4, e.clientX - d.offsetX));
    const y = Math.max(4, Math.min(window.innerHeight - SIZE - 4, e.clientY - d.offsetY));
    d.lastPos = { x, y };
    setBtnPos(d.lastPos);
  };
  const onBtnPointerUp = () => {
    const d = dragRef.current;
    if (!d.dragging) return;
    d.dragging = false;
    if (d.moved) {
      if (d.lastPos) { try { localStorage.setItem('gunaydin_btn_pos', JSON.stringify(d.lastPos)); } catch (_) {} }
    } else {
      setOpen(true); // sürüklenmediyse = tıklama → paneli aç
    }
  };

  if (!enabled) return null;

  return (
    <>
      {/* Floating ikon — sadece simge, sürüklenebilir (tıkla → aç, sürükle → taşı) */}
      <button
        onPointerDown={onBtnPointerDown}
        onPointerMove={onBtnPointerMove}
        onPointerUp={onBtnPointerUp}
        style={btnPos ? { left: btnPos.x, top: btnPos.y, right: 'auto', bottom: 'auto' } : undefined}
        className={`fixed ${btnPos ? '' : 'bottom-5 right-5'} z-40 w-14 h-14 rounded-full bg-gradient-to-r from-amber-400 to-orange-500 text-white shadow-lg hover:shadow-xl transition-shadow flex items-center justify-center cursor-grab active:cursor-grabbing touch-none select-none`}
        title="Günaydın Paneli — tıkla aç, sürükleyerek taşı"
        data-testid="gunaydin-open-btn"
      >
        <Sun className="w-6 h-6 pointer-events-none" />
      </button>

      {!open && null}

      {open && (
        <div className="fixed inset-0 z-[60] bg-slate-900/70 backdrop-blur-sm flex items-center justify-center p-4 animate-[fadeIn_0.2s]"
             data-testid="gunaydin-modal">
          <div className="bg-white w-full max-w-6xl h-[92vh] rounded-2xl shadow-2xl flex flex-col overflow-hidden">
            {/* Header */}
            <div className="bg-gradient-to-r from-amber-500 via-orange-500 to-rose-500 text-white px-6 py-4 flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-full bg-white/20 flex items-center justify-center">
                  <Sun className="w-5 h-5" />
                </div>
                <div>
                  <h2 className="text-lg font-bold tracking-wide">Günaydın{user?.ad_soyad || user?.name ? `, ${user.ad_soyad || user.name}` : ''} 👋</h2>
                  <p className="text-xs text-white/90">{new Date().toLocaleDateString('tr-TR', { weekday: 'long', day: '2-digit', month: 'long', year: 'numeric' })}{data?.firma_filtresi ? ` • ${data.firma_filtresi}` : ''}</p>
                </div>
              </div>
              <div className="flex items-center gap-2">
                <button
                  onClick={handleCariSync}
                  disabled={syncLoading}
                  className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-white/20 hover:bg-white/30 text-white text-xs font-semibold transition disabled:opacity-60"
                  title="Logo'dan cari bakiye ve hareketleri çek"
                  data-testid="gunaydin-sync-btn"
                >
                  {syncLoading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <DownloadCloud className="w-3.5 h-3.5" />}
                  {syncLoading ? 'Güncelleniyor...' : 'Cariyi Güncelle'}
                </button>
                <button
                  onClick={() => { setData(null); fetchData(); }}
                  className="p-2 rounded-lg hover:bg-white/20 transition"
                  title="Ekranı yenile"
                >
                  <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
                </button>
                <button
                  onClick={handleClose}
                  className="p-2 rounded-lg hover:bg-white/20 transition"
                  title="Kapat"
                  data-testid="gunaydin-close-btn"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>
            </div>

            {/* Üst özet kartları */}
            <div className="px-6 py-4 bg-slate-50 border-b grid grid-cols-2 lg:grid-cols-5 gap-3">
              <KpiCard
                icon={<AlertTriangle className="w-4 h-4" />}
                label="Vadesi Geçmiş"
                value={`${fmtMoney(data?.vadesi_gecmis?.toplam_tutar)} ₺`}
                sub={`${data?.vadesi_gecmis?.musteri_sayisi || 0} müşteri`}
                color="rose"
              />
              <KpiCard
                icon={<Wallet className="w-4 h-4" />}
                label="Toplam Bakiye"
                value={`${fmtMoney(data?.vadesi_gecmis?.toplam_bakiye)} ₺`}
                sub="vadesi geçmişi olan carilerin"
                color="slate"
              />
              <KpiCard
                icon={<TrendingUp className="w-4 h-4" />}
                label="Kritik (30+ gün)"
                value={`${fmtMoney(data?.vadesi_gecmis?.kritik_tutar)} ₺`}
                sub="yüksek risk"
                color="red"
              />
              <KpiCard
                icon={<Calendar className="w-4 h-4" />}
                label="Bu Hafta Vade"
                value={`${fmtMoney(data?.bu_hafta_vade?.toplam_tutar)} ₺`}
                sub={`${data?.bu_hafta_vade?.kayit_sayisi || 0} fatura`}
                color="amber"
              />
              <KpiCard
                icon={<Package className="w-4 h-4" />}
                label="Bekleyen Sipariş"
                value={ozet.bekleyen_siparis || 0}
                sub={`bugün ${ozet.bugun_siparis_sayisi || 0} sip. • ${fmtMoney(ozet.bugun_siparis_tutar)} ₺`}
                color="blue"
              />
            </div>

            {/* Sekmeler */}
            <div className="px-6 pt-3 bg-white border-b flex gap-1 items-center">
              <TabBtn active={aktifSekme === 'vadesi_gecmis'} onClick={() => setAktifSekme('vadesi_gecmis')} icon={<AlertTriangle className="w-3.5 h-3.5" />} label="Vadesi Geçmiş" count={vadeList.length} />
              <TabBtn active={aktifSekme === 'bu_hafta'} onClick={() => setAktifSekme('bu_hafta')} icon={<Calendar className="w-3.5 h-3.5" />} label="Bu Hafta Plan" count={haftaList.length} />
              <TabBtn active={aktifSekme === 'ozet'} onClick={() => setAktifSekme('ozet')} icon={<TrendingUp className="w-3.5 h-3.5" />} label="Günün Özeti" />
              <TabBtn active={aktifSekme === 'uyarilar'} onClick={() => setAktifSekme('uyarilar')} icon={<Bell className="w-3.5 h-3.5" />} label="Kritik Uyarılar" count={(uyarilar.dusuk_stok?.length || 0) + (uyarilar.risk_asimi?.length || 0) + (uyarilar.resimsiz_stoklu?.length || 0) + (uyarilar.pasif_musteri?.length || 0) + (uyarilar.uzun_sepet?.length || 0)} />
              <TabBtn active={aktifSekme === 'resim'} onClick={() => setAktifSekme('resim')} icon={<Camera className="w-3.5 h-3.5" />} label="Resim Çekimi" />
              <a
                href="/crm/tasks?kapsam=benden"
                target="_blank"
                rel="noopener noreferrer"
                className="ml-auto inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-violet-600 hover:bg-violet-700 text-white text-[11px] font-bold transition shadow-sm mb-2"
                data-testid="gunaydin-crm-atadigim-link"
                title="Atadığım görevleri CRM'de yönet"
              >
                <UserPlus className="w-3.5 h-3.5" /> Atadıklarım (CRM)
                {(atadigimGorevler.ozet?.bekliyor || 0) > 0 && (
                  <span className="ml-1 px-1.5 py-0.5 rounded-full bg-white/30 text-[10px] font-black">
                    {atadigimGorevler.ozet?.bekliyor}
                  </span>
                )}
              </a>
            </div>

            {/* İçerik */}
            <div className="flex-1 overflow-y-auto px-6 py-4 bg-slate-50">
              {loading && (
                <div className="flex items-center justify-center h-full text-slate-500">
                  <Loader2 className="w-6 h-6 animate-spin mr-2" />
                  Veriler toplanıyor...
                </div>
              )}
              {!loading && aktifSekme === 'vadesi_gecmis' && (
                <VadesiGecmisTablo
                  rows={vadeList}
                  selectedIds={selectedIds}
                  onToggle={toggleSelect}
                  waUrl={waUrl}
                  mailtoUrl={mailtoUrl}
                  onGorevAta={(r) => setGorevModal({ row: r, kaynak: 'vade' })}
                />
              )}
              {!loading && aktifSekme === 'bu_hafta' && (
                <BuHaftaTablo rows={haftaList} waUrl={waUrl} onGorevAta={(r) => setGorevModal({ row: r, kaynak: 'hafta' })} />
              )}
              {!loading && aktifSekme === 'ozet' && (
                <OzetPanel ozet={ozet} yeniMusteriler={yeniMusteriler} />
              )}
              {!loading && aktifSekme === 'uyarilar' && (
                <UyarilarPanel uyarilar={uyarilar} onGorevAta={(row, tip) => setGorevModal({ row, tip })} />
              )}
              {aktifSekme === 'resim' && (
                <ResimRaporPanel />
              )}
            </div>

            {/* Footer aksiyonlar */}
            <div className="bg-white border-t px-6 py-3 flex items-center justify-between gap-2">
              <div className="flex items-center gap-2 text-xs text-slate-500">
                {aktifSekme === 'vadesi_gecmis' && selectedIds.length > 0 && (
                  <span className="font-semibold text-rose-700">{selectedIds.length} seçili</span>
                )}
              </div>
              <div className="flex items-center gap-2 flex-wrap justify-end">
                {aktifSekme === 'vadesi_gecmis' && (
                  <button
                    onClick={handleWhatsappToplu}
                    disabled={selectedIds.length === 0}
                    className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-green-600 text-white text-xs font-semibold hover:bg-green-700 transition disabled:opacity-40 disabled:cursor-not-allowed"
                    data-testid="gunaydin-wa-btn"
                  >
                    <MessageCircle className="w-3.5 h-3.5" />
                    WhatsApp
                  </button>
                )}
                <button
                  onClick={handleExcel}
                  className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-emerald-600 text-white text-xs font-semibold hover:bg-emerald-700 transition"
                  data-testid="gunaydin-excel-btn"
                >
                  <Download className="w-3.5 h-3.5" />
                  Excel İndir
                </button>
                <button
                  onClick={handleReaçOnce}
                  className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-white border border-slate-300 text-slate-700 text-xs font-semibold hover:bg-slate-100 transition"
                  title="Yarın tekrar açılsın"
                >
                  <Clock className="w-3.5 h-3.5" />
                  Yarın Tekrar Aç
                </button>
                <button
                  onClick={handleClose}
                  className="inline-flex items-center gap-1.5 px-4 py-1.5 rounded-lg bg-slate-800 text-white text-xs font-bold hover:bg-slate-900 transition"
                  data-testid="gunaydin-close-today-btn"
                >
                  Bugün İçin Kapat
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Görev ata modal */}
      {gorevModal && (
        <GorevAtaModal
          satir={gorevModal.row}
          tip={gorevModal.tip || 'tahsilat'}
          personeller={personeller}
          onClose={() => setGorevModal(null)}
          onSubmit={handleGorevAta}
        />
      )}

      {/* Görev durum güncelleme modal */}
      {gorevDurumModal && (
        <GorevDurumModal
          task={gorevDurumModal}
          onClose={() => setGorevDurumModal(null)}
          onSubmit={handleGorevDurum}
        />
      )}
    </>
  );
};

// ============ Alt bileşenler ============

const KpiCard = ({ icon, label, value, sub, color }) => {
  const colorMap = {
    rose: 'from-rose-500 to-red-600',
    red: 'from-red-600 to-red-800',
    amber: 'from-amber-500 to-orange-600',
    blue: 'from-sky-500 to-blue-600',
    slate: 'from-slate-600 to-slate-800',
  };
  return (
    <div className="bg-white rounded-xl border border-slate-200 p-3 shadow-sm">
      <div className="flex items-center gap-2">
        <div className={`w-8 h-8 rounded-lg bg-gradient-to-br ${colorMap[color]} text-white flex items-center justify-center`}>
          {icon}
        </div>
        <div className="flex-1 min-w-0">
          <p className="text-[10px] text-slate-500 uppercase tracking-wide truncate">{label}</p>
          <p className="text-sm font-black text-slate-900 truncate">{value}</p>
          <p className="text-[10px] text-slate-400 truncate">{sub}</p>
        </div>
      </div>
    </div>
  );
};

const TabBtn = ({ active, onClick, icon, label, count }) => (
  <button
    onClick={onClick}
    className={`inline-flex items-center gap-1.5 px-3 py-2 text-xs font-semibold border-b-2 transition ${active ? 'border-orange-500 text-orange-700' : 'border-transparent text-slate-500 hover:text-slate-800'}`}
  >
    {icon}
    {label}
    {typeof count === 'number' && count > 0 && (
      <span className={`px-1.5 py-0.5 rounded-full text-[10px] ${active ? 'bg-orange-100 text-orange-700' : 'bg-slate-100 text-slate-600'}`}>
        {count}
      </span>
    )}
  </button>
);

const RiskBadge = ({ level, gun, title }) => {
  const map = {
    kritik: 'bg-red-100 text-red-800 border-red-300',
    yuksek: 'bg-orange-100 text-orange-800 border-orange-300',
    normal: 'bg-amber-50 text-amber-800 border-amber-200',
  };
  return (
    <span
      title={title}
      className={`px-1.5 py-0.5 rounded-full text-[10px] font-bold border cursor-help ${map[level] || map.normal}`}
    >
      ~{gun} gün
    </span>
  );
};

const VadesiGecmisTablo = ({ rows, selectedIds, onToggle, waUrl, mailtoUrl, onGorevAta }) => {
  if (!rows.length) {
    return (
      <div className="bg-white rounded-xl border border-slate-200 p-8 text-center">
        <div className="w-14 h-14 rounded-full bg-emerald-100 flex items-center justify-center mx-auto mb-3">
          <TrendingUp className="w-7 h-7 text-emerald-600" />
        </div>
        <p className="text-slate-700 font-semibold">Harika! Vadesi geçmiş alacağınız yok 🎉</p>
      </div>
    );
  }
  return (
    <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
      <table className="w-full text-xs">
        <thead className="bg-slate-100 text-slate-700">
          <tr>
            <th className="px-2 py-2 text-left w-8"></th>
            <th className="px-2 py-2 text-left">Müşteri</th>
            <th className="px-2 py-2 text-left">Firma</th>
            <th className="px-2 py-2 text-center">Gecikme</th>
            <th className="px-2 py-2 text-center">Fatura</th>
            <th className="px-2 py-2 text-right">Günü Geçen (₺)</th>
            <th className="px-2 py-2 text-right" title="Carinin toplam bakiyesi (borç − alacak, vadesi gelmemiş dahil)">Toplam Bakiye (₺)</th>
            <th className="px-2 py-2 text-center">Aksiyonlar</th>
          </tr>
        </thead>
        <tbody className="divide-y divide-slate-100">
          {rows.map((r) => {
            const sel = selectedIds.includes(r.user_id);
            return (
              <tr key={r.user_id} className={`hover:bg-slate-50 ${sel ? 'bg-rose-50/60' : ''}`}>
                <td className="px-2 py-2">
                  <input type="checkbox" checked={sel} onChange={() => onToggle(r.user_id)}
                         data-testid={`gunaydin-vade-check-${r.user_id}`}
                         className="accent-rose-600" />
                </td>
                <td className="px-2 py-2">
                  <div className="font-semibold text-slate-800 truncate max-w-[260px]">{r.musteri_ad || '-'}</div>
                  <div className="text-[10px] text-slate-500 font-mono">{r.cari_kodu}{r.telefon ? ` • ${r.telefon}` : ''}</div>
                </td>
                <td className="px-2 py-2 text-slate-600">{r.firma || '-'}</td>
                <td className="px-2 py-2 text-center">
                  <RiskBadge level={r.risk_seviyesi} gun={r.gecikme_gun} title={`Ortalama gecikme: ${r.gecikme_gun} gün • En eski fatura: ${r.en_eski_gun || r.gecikme_gun} gün gecikmiş`} />
                </td>
                <td className="px-2 py-2 text-center font-bold">{r.fatura_sayisi}</td>
                <td className="px-2 py-2 text-right font-bold text-rose-700">{fmtMoney(r.tutar)}</td>
                <td className="px-2 py-2 text-right font-semibold text-slate-800">{fmtMoney(r.bakiye)}</td>
                <td className="px-2 py-2">
                  <div className="flex items-center justify-center gap-1">
                    {r.telefon && (
                      <>
                        <a href={`tel:${r.telefon}`} className="p-1 rounded hover:bg-sky-100 text-sky-700" title="Ara">
                          <Phone className="w-3.5 h-3.5" />
                        </a>
                        {waUrl(r) && (
                          <a href={waUrl(r)} target="_blank" rel="noopener noreferrer"
                             className="p-1 rounded hover:bg-green-100 text-green-700" title="WhatsApp">
                            <MessageCircle className="w-3.5 h-3.5" />
                          </a>
                        )}
                      </>
                    )}
                    {mailtoUrl(r) && (
                      <a href={mailtoUrl(r)} className="p-1 rounded hover:bg-slate-200 text-slate-700" title="E-posta">
                        <Mail className="w-3.5 h-3.5" />
                      </a>
                    )}
                    <button
                      onClick={() => onGorevAta(r)}
                      className="p-1 rounded hover:bg-orange-100 text-orange-700"
                      title="Personele görev ata"
                      data-testid={`gunaydin-gorev-btn-${r.user_id}`}
                    >
                      <UserPlus className="w-3.5 h-3.5" />
                    </button>
                  </div>
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
};

const BuHaftaTablo = ({ rows, waUrl, onGorevAta }) => {
  if (!rows.length) {
    return (
      <div className="bg-white rounded-xl border p-8 text-center text-slate-500">
        Bu hafta vadesi gelen açık fatura yok
      </div>
    );
  }
  // Güne göre grupla
  const groups = rows.reduce((acc, r) => {
    const k = r.vade_tarihi?.slice(0, 10) || 'diger';
    if (!acc[k]) acc[k] = [];
    acc[k].push(r);
    return acc;
  }, {});
  const keys = Object.keys(groups).sort();

  return (
    <div className="space-y-3">
      {keys.map(k => {
        const gunRows = groups[k];
        const gunToplam = gunRows.reduce((s, r) => s + (r.tutar || 0), 0);
        return (
          <div key={k} className="bg-white rounded-xl border overflow-hidden">
            <div className="bg-slate-100 px-3 py-2 flex items-center justify-between">
              <div className="flex items-center gap-2 text-xs font-bold text-slate-700">
                <Calendar className="w-3.5 h-3.5" />
                {new Date(k).toLocaleDateString('tr-TR', { weekday: 'long', day: '2-digit', month: 'long' })}
                <span className="bg-slate-200 px-1.5 py-0.5 rounded text-[10px]">{gunRows.length}</span>
              </div>
              <div className="text-xs font-bold text-amber-700">{fmtMoney(gunToplam)} ₺</div>
            </div>
            <table className="w-full text-xs">
              <tbody className="divide-y divide-slate-100">
                {gunRows.map((r, i) => (
                  <tr key={i} className="hover:bg-slate-50">
                    <td className="px-3 py-2">
                      <div className="font-semibold text-slate-800 truncate max-w-[300px]">{r.musteri_ad}</div>
                      <div className="text-[10px] text-slate-500 font-mono">{r.cari_kodu} • {r.firma}</div>
                    </td>
                    <td className="px-3 py-2 text-slate-600 font-mono">{r.fatura_no || '—'}</td>
                    <td className="px-3 py-2 text-right font-bold text-slate-800">{fmtMoney(r.tutar)} ₺</td>
                    <td className="px-3 py-2">
                      <div className="flex items-center justify-end gap-1">
                        {r.telefon && waUrl(r) && (
                          <a href={waUrl(r)} target="_blank" rel="noopener noreferrer"
                             className="p-1 rounded hover:bg-green-100 text-green-700" title="WhatsApp">
                            <MessageCircle className="w-3.5 h-3.5" />
                          </a>
                        )}
                        <button
                          onClick={() => onGorevAta(r)}
                          className="p-1 rounded hover:bg-orange-100 text-orange-700"
                          title="Görev ata"
                        >
                          <UserPlus className="w-3.5 h-3.5" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        );
      })}
    </div>
  );
};

const OzetPanel = ({ ozet, yeniMusteriler }) => {
  const [aktifKart, setAktifKart] = useState(null);

  const detaylar = {
    bugun_siparis: {
      title: 'Bugünkü Siparişler',
      content: (
        <div>
          <p className="text-sm"><strong>{ozet.bugun_siparis_sayisi || 0}</strong> adet sipariş alındı.</p>
          <p className="text-sm">Toplam ciro: <strong className="text-emerald-700">{fmtMoney(ozet.bugun_siparis_tutar)} ₺</strong></p>
          <p className="text-[11px] text-slate-500 mt-2">Sipariş yönetimi sayfasından detaylı liste görebilirsiniz.</p>
          <a href="/panel/siparisler" target="_blank" rel="noopener noreferrer" className="inline-block mt-2 text-xs text-orange-600 font-bold hover:underline">→ Sipariş Yönetimi'ne git</a>
        </div>
      )
    },
    dun_siparis: {
      title: 'Dünkü Siparişler',
      content: (
        <div>
          <p className="text-sm"><strong>{ozet.dun_siparis_sayisi || 0}</strong> adet sipariş alındı.</p>
          <p className="text-sm">Toplam ciro: <strong className="text-sky-700">{fmtMoney(ozet.dun_siparis_tutar)} ₺</strong></p>
          {(ozet.bugun_siparis_sayisi || 0) > 0 && (
            <p className="text-[11px] text-slate-500 mt-2">Bugüne göre fark: <strong>{((ozet.bugun_siparis_sayisi || 0) - (ozet.dun_siparis_sayisi || 0))}</strong> sipariş</p>
          )}
        </div>
      )
    },
    hafta_siparis: {
      title: 'Son 7 Gün Siparişler',
      content: (
        <div>
          <p className="text-sm"><strong>{ozet.hafta_siparis_sayisi || 0}</strong> adet sipariş alındı.</p>
          <p className="text-sm">Toplam ciro: <strong className="text-violet-700">{fmtMoney(ozet.hafta_siparis_tutar)} ₺</strong></p>
          {(ozet.hafta_siparis_sayisi || 0) > 0 && (
            <p className="text-[11px] text-slate-500 mt-2">Günlük ortalama: <strong>{Math.round((ozet.hafta_siparis_sayisi || 0) / 7)}</strong> sipariş • <strong>{fmtMoney((ozet.hafta_siparis_tutar || 0) / 7)} ₺</strong></p>
          )}
        </div>
      )
    },
    bekleyen_siparis: {
      title: 'Bekleyen Siparişler',
      content: (
        <div>
          <p className="text-sm"><strong>{ozet.bekleyen_siparis || 0}</strong> sipariş onay/hazırlama bekliyor.</p>
          <p className="text-[11px] text-slate-500 mt-2">Bu siparişler henüz WMS'e veya Logo'ya işlenmedi.</p>
          <a href="/wms/siparis-monitor" target="_blank" rel="noopener noreferrer" className="inline-block mt-2 text-xs text-amber-600 font-bold hover:underline">→ WMS Sipariş Monitörü</a>
        </div>
      )
    },
    aktif_sepet: {
      title: 'Aktif Sepetler',
      content: (
        <div>
          <p className="text-sm"><strong>{ozet.aktif_sepet_sayisi || 0}</strong> müşterinin sepetinde ürün var.</p>
          <p className="text-sm">Potansiyel ciro: <strong className="text-orange-700">{fmtMoney(ozet.aktif_sepet_tutar)} ₺</strong></p>
          <p className="text-[11px] text-slate-500 mt-2">"Açık Sepet 3+ gün" kritik uyarısında müşterilere arama görevi atayabilirsiniz.</p>
        </div>
      )
    },
    bugun_aktif: {
      title: 'Bugün Aktif Kullanıcılar',
      content: (
        <div>
          <p className="text-sm"><strong>{ozet.bugun_aktif_kullanici || 0}</strong> müşteri bugün giriş yaptı.</p>
          <p className="text-sm">Toplam müşteri: <strong>{ozet.toplam_musteri || 0}</strong></p>
          {(ozet.toplam_musteri || 0) > 0 && (
            <p className="text-[11px] text-slate-500 mt-2">Aktif oranı: <strong>%{Math.round((ozet.bugun_aktif_kullanici / ozet.toplam_musteri) * 100)}</strong></p>
          )}
        </div>
      )
    },
    aday_musteri: {
      title: 'Aday Müşteriler',
      content: (
        <div>
          <p className="text-sm"><strong>{ozet.aday_musteri_sayisi || 0}</strong> aday müşteri Logo cari eşleşmesi bekliyor.</p>
          <p className="text-[11px] text-slate-500 mt-2">CRM saha ziyaretinden panel açılan adaylar burada görünür.</p>
          <a href="/panel/musteriler" target="_blank" rel="noopener noreferrer" className="inline-block mt-2 text-xs text-amber-600 font-bold hover:underline">→ Panel Müşterileri (eşleşme yapın)</a>
        </div>
      )
    },
  };

  return (
    <div className="space-y-4">
      {/* Satış istatistikleri */}
      <div>
        <h4 className="text-[11px] font-bold text-slate-700 uppercase tracking-wide mb-2">📈 Satış İstatistikleri (tıklayınca detay)</h4>
        <div className="grid grid-cols-3 gap-3">
          <BigStat label="Bugün Sipariş" value={ozet.bugun_siparis_sayisi || 0} sub={`${fmtMoney(ozet.bugun_siparis_tutar)} ₺`} color="emerald" onClick={() => setAktifKart('bugun_siparis')} />
          <BigStat label="Dün Sipariş" value={ozet.dun_siparis_sayisi || 0} sub={`${fmtMoney(ozet.dun_siparis_tutar)} ₺`} color="sky" onClick={() => setAktifKart('dun_siparis')} />
          <BigStat label="Son 7 Gün" value={ozet.hafta_siparis_sayisi || 0} sub={`${fmtMoney(ozet.hafta_siparis_tutar)} ₺`} color="violet" onClick={() => setAktifKart('hafta_siparis')} />
        </div>
      </div>

      {/* Operasyonel */}
      <div>
        <h4 className="text-[11px] font-bold text-slate-700 uppercase tracking-wide mb-2">⚙️ Operasyonel Durum (tıklayınca detay)</h4>
        <div className="grid grid-cols-4 gap-3">
          <BigStat label="Bekleyen Sipariş" value={ozet.bekleyen_siparis || 0} color="amber" sub="WMS / onay" onClick={() => setAktifKart('bekleyen_siparis')} />
          <BigStat label="Aktif Sepet" value={ozet.aktif_sepet_sayisi || 0} color="orange" sub={`${fmtMoney(ozet.aktif_sepet_tutar)} ₺ pot.`} onClick={() => setAktifKart('aktif_sepet')} />
          <BigStat label="Bugün Aktif" value={ozet.bugun_aktif_kullanici || 0} color="cyan" sub={`${ozet.toplam_musteri || 0} müşteri`} onClick={() => setAktifKart('bugun_aktif')} />
          <BigStat label="Aday Müşteri" value={ozet.aday_musteri_sayisi || 0} color="amber" sub="cari bekliyor" onClick={() => setAktifKart('aday_musteri')} />
        </div>
      </div>

      {/* Detay popup */}
      {aktifKart && detaylar[aktifKart] && (
        <div className="fixed inset-0 z-[68] bg-slate-900/50 flex items-center justify-center p-4" onClick={() => setAktifKart(null)}>
          <div className="bg-white rounded-xl w-full max-w-md shadow-2xl" onClick={e => e.stopPropagation()}>
            <div className="bg-slate-800 text-white px-4 py-3 rounded-t-xl flex items-center justify-between">
              <h3 className="font-bold text-sm">{detaylar[aktifKart].title}</h3>
              <button onClick={() => setAktifKart(null)} className="p-1 hover:bg-white/20 rounded"><X className="w-4 h-4" /></button>
            </div>
            <div className="p-4">{detaylar[aktifKart].content}</div>
          </div>
        </div>
      )}

      {/* Yeni Müşteriler — detay tablosu */}
      {yeniMusteriler && yeniMusteriler.length > 0 && (
        <div className="bg-white rounded-xl border overflow-hidden">
          <div className="bg-emerald-50 border-b border-emerald-200 px-3 py-2 text-xs font-bold text-emerald-800 flex items-center justify-between">
            <span className="flex items-center gap-2">⭐ Yeni Müşteriler ({ozet.yeni_musteri_sayisi || 0})</span>
            <span className="text-[10px] font-normal text-emerald-700">İlk 5 siparişe kadar yeni rozeti</span>
          </div>
          <table className="w-full text-xs">
            <thead className="bg-slate-50 text-slate-600">
              <tr>
                <th className="px-3 py-1.5 text-left font-semibold">Müşteri</th>
                <th className="px-3 py-1.5 text-left font-semibold">Cari Kodu</th>
                <th className="px-3 py-1.5 text-left font-semibold">Firma</th>
                <th className="px-3 py-1.5 text-left font-semibold">Telefon</th>
                <th className="px-3 py-1.5 text-left font-semibold">Eşleşme Tarihi</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {yeniMusteriler.map((m, i) => (
                <tr key={i} className="hover:bg-slate-50">
                  <td className="px-3 py-1.5 font-semibold text-slate-800 truncate max-w-[280px]">{m.musteri_ad}</td>
                  <td className="px-3 py-1.5 font-mono text-slate-600">{m.cari_kodu || '-'}</td>
                  <td className="px-3 py-1.5 text-slate-600">{m.firma}</td>
                  <td className="px-3 py-1.5 text-slate-600">{m.telefon || '-'}</td>
                  <td className="px-3 py-1.5 text-slate-500 text-[11px]">{m.tarih ? new Date(m.tarih).toLocaleDateString('tr-TR') : '-'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

const BigStat = ({ label, value, sub, color, onClick }) => {
  const map = {
    emerald: 'from-emerald-500 to-teal-600',
    sky: 'from-sky-500 to-blue-600',
    violet: 'from-violet-500 to-purple-600',
    amber: 'from-amber-500 to-orange-600',
    orange: 'from-orange-500 to-rose-600',
    cyan: 'from-cyan-500 to-sky-600',
  };
  const Tag = onClick ? 'button' : 'div';
  return (
    <Tag
      onClick={onClick}
      className={`bg-white rounded-xl border border-slate-200 overflow-hidden shadow-sm text-left ${onClick ? 'hover:shadow-md hover:scale-[1.02] transition-all cursor-pointer' : ''}`}
    >
      <div className={`bg-gradient-to-br ${map[color]} px-3 py-1.5 text-white text-[10px] font-bold uppercase tracking-wide flex items-center justify-between`}>
        <span>{label}</span>
        {onClick && <span className="text-[14px] opacity-80">→</span>}
      </div>
      <div className="p-3">
        <div className="text-2xl font-black text-slate-900">{typeof value === 'number' ? value.toLocaleString('tr-TR') : value}</div>
        {sub && <div className="text-[11px] text-slate-500 mt-0.5">{sub}</div>}
      </div>
    </Tag>
  );
};

const UyarilarPanel = ({ uyarilar, onGorevAta }) => {
  const sections = [
    {
      key: 'dusuk_stok',
      title: '📦 Düşük Stok Uyarısı',
      headerCls: 'bg-red-50 border-red-200 text-red-800',
      empty: 'Düşük stok uyarısı yok',
      rows: uyarilar.dusuk_stok || [],
      gorevTip: null,
      render: (p, i) => (
        <tr key={i} className="hover:bg-slate-50">
          <td className="px-3 py-2 font-mono text-slate-600 text-[11px]">{p.sku}</td>
          <td className="px-3 py-2 text-slate-800 truncate max-w-[400px]">{p.urun_adi}</td>
          <td className="px-3 py-2 text-right font-bold text-red-600">Stok: {p.toplam_stok}</td>
          <td className="px-3 py-2 text-right text-slate-500">Aylık: {p.aylik_satis}</td>
        </tr>
      ),
    },
    {
      key: 'resimsiz_stoklu',
      title: '🖼️ Resimsiz Stoklu Ürün',
      headerCls: 'bg-purple-50 border-purple-200 text-purple-800',
      empty: 'Stoklu ürünlerin tümünde görsel mevcut',
      rows: uyarilar.resimsiz_stoklu || [],
      gorevTip: 'resim',
      render: (p, i) => (
        <tr key={i} className="hover:bg-slate-50">
          <td className="px-3 py-2 font-mono text-slate-600 text-[11px]">{p.sku}</td>
          <td className="px-3 py-2 text-slate-800 truncate max-w-[400px]">{p.urun_adi}</td>
          <td className="px-3 py-2 text-center">
            {p.grup && <span className="px-1.5 py-0.5 rounded bg-slate-100 text-[10px] font-mono">{p.grup}</span>}
          </td>
          <td className="px-3 py-2 text-right font-bold text-purple-700">Stok: {p.toplam_stok}</td>
          <td className="px-3 py-2 text-right">
            <button
              onClick={() => onGorevAta({ sku: p.sku, musteri_ad: p.urun_adi, cari_kodu: '', firma: '', tutar: 0, gecikme_gun: 0 }, 'resim')}
              className="p-1 rounded hover:bg-purple-100 text-purple-700"
              title="Görsel temin için görev ata"
            >
              <UserPlus className="w-3.5 h-3.5" />
            </button>
          </td>
        </tr>
      ),
    },
    {
      key: 'risk_asimi',
      title: '⚠️ Risk Limiti Aşımı',
      headerCls: 'bg-orange-50 border-orange-200 text-orange-800',
      empty: 'Risk limiti aşımı yok',
      rows: uyarilar.risk_asimi || [],
      gorevTip: 'tahsilat',
      render: (r, i) => (
        <tr key={i} className="hover:bg-slate-50">
          <td className="px-3 py-2 font-semibold text-slate-800 truncate max-w-[260px]">{r.musteri_ad}</td>
          <td className="px-3 py-2 text-slate-500 font-mono text-[11px]">{r.cari_kodu}</td>
          <td className="px-3 py-2 text-slate-500">{r.firma}</td>
          <td className="px-3 py-2 text-right">Limit: {fmtMoney(r.limit)} ₺</td>
          <td className="px-3 py-2 text-right font-bold text-rose-700">Aşım: {fmtMoney(r.asim_tutari)} ₺</td>
          <td className="px-3 py-2 text-right">
            <button
              onClick={() => onGorevAta({ ...r, tutar: r.acik, gecikme_gun: 0 }, 'tahsilat')}
              className="p-1 rounded hover:bg-orange-100 text-orange-700"
              title="Tahsilat görevi ata"
            >
              <UserPlus className="w-3.5 h-3.5" />
            </button>
          </td>
        </tr>
      ),
    },
    {
      key: 'pasif_musteri',
      title: '💤 Uzun Süredir Login Olmayan Müşteriler',
      headerCls: 'bg-slate-100 border-slate-200 text-slate-800',
      empty: 'Tüm aktif müşteriler son 30 gün içinde giriş yapmış',
      rows: uyarilar.pasif_musteri || [],
      gorevTip: 'arama',
      render: (m, i) => (
        <tr key={i} className="hover:bg-slate-50">
          <td className="px-3 py-2 font-semibold text-slate-800 truncate max-w-[260px]">{m.musteri_ad}</td>
          <td className="px-3 py-2 text-slate-500 font-mono text-[11px]">{m.cari_kodu || '—'}</td>
          <td className="px-3 py-2 text-slate-500">{m.firma}</td>
          <td className="px-3 py-2 text-slate-600">{m.telefon || '—'}</td>
          <td className="px-3 py-2 text-right font-bold text-slate-700">{m.gun >= 999 ? 'Hiç' : `${m.gun} gün`}</td>
          <td className="px-3 py-2 text-right">
            <button
              onClick={() => onGorevAta({ ...m, tutar: 0, gecikme_gun: m.gun }, 'arama')}
              className="p-1 rounded hover:bg-slate-200 text-slate-700"
              title="Arama görevi ata"
            >
              <UserPlus className="w-3.5 h-3.5" />
            </button>
          </td>
        </tr>
      ),
    },
    {
      key: 'uzun_sepet',
      title: '🛒 Açık Bekleyen Sepetler (3+ gün)',
      headerCls: 'bg-amber-50 border-amber-200 text-amber-800',
      empty: 'Bekleyen açık sepet yok',
      rows: uyarilar.uzun_sepet || [],
      gorevTip: 'gorusme',
      render: (m, i) => (
        <tr key={i} className="hover:bg-slate-50">
          <td className="px-3 py-2 font-semibold text-slate-800 truncate max-w-[260px]">{m.musteri_ad}</td>
          <td className="px-3 py-2 text-slate-500">{m.firma}</td>
          <td className="px-3 py-2 text-slate-600">{m.telefon || '—'}</td>
          <td className="px-3 py-2 text-right text-slate-500">{m.urun_adet} ürün</td>
          <td className="px-3 py-2 text-right font-bold text-amber-700">{fmtMoney(m.tutar)} ₺</td>
          <td className="px-3 py-2 text-right">
            <button
              onClick={() => onGorevAta({ ...m, cari_kodu: m.cari_kodu || '', tutar: m.tutar, gecikme_gun: 0 }, 'gorusme')}
              className="p-1 rounded hover:bg-amber-100 text-amber-700"
              title="Görüşme görevi ata"
            >
              <UserPlus className="w-3.5 h-3.5" />
            </button>
          </td>
        </tr>
      ),
    },
  ];

  const toplam = sections.reduce((s, sec) => s + sec.rows.length, 0);
  if (toplam === 0) {
    return (
      <div className="bg-white rounded-xl border p-8 text-center text-slate-500">
        Tüm sistem yeşil 👍 Kritik uyarı yok.
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {sections.map(sec => sec.rows.length > 0 && (
        <div key={sec.key} className="bg-white rounded-xl border overflow-hidden">
          <div className={`border-b px-3 py-2 text-xs font-bold flex items-center justify-between ${sec.headerCls}`}>
            <span>{sec.title}</span>
            <span className="text-[10px] font-normal opacity-80">{sec.rows.length} kayıt</span>
          </div>
          <table className="w-full text-xs">
            <tbody className="divide-y">
              {sec.rows.slice(0, 25).map(sec.render)}
            </tbody>
          </table>
          {sec.rows.length > 25 && (
            <div className="px-3 py-2 bg-slate-50 text-center text-[10px] text-slate-500">
              ...ve {sec.rows.length - 25} kayıt daha
            </div>
          )}
        </div>
      ))}
    </div>
  );
};

// ========= Atadığım Görevler Panel — CRM yönlendirmeli =========
const ResimRaporPanel = () => {
  const [period, setPeriod] = useState('gunluk');
  const [rapor, setRapor] = useState(null);
  const [yukleniyor, setYukleniyor] = useState(false);

  const fetchRapor = useCallback(async (p) => {
    setYukleniyor(true);
    try {
      const token = localStorage.getItem('token');
      const res = await axios.get(`${API}/panel/gunaydin-resim-raporu?period=${p}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setRapor(res.data?.success ? res.data : { kisiler: [], toplam_resim: 0, toplam_kisi: 0 });
    } catch (e) {
      setRapor({ kisiler: [], toplam_resim: 0, toplam_kisi: 0 });
    } finally {
      setYukleniyor(false);
    }
  }, []);

  useEffect(() => { fetchRapor(period); }, [period, fetchRapor]);

  const kisiler = rapor?.kisiler || [];
  // "Az çeken" eşiği: dönemde günlük ortalaması < 5 veya (bugün görünümünde) toplam < 3
  const azEsik = period === 'gunluk' ? 3 : 5;
  const fmtDt = (s) => {
    if (!s) return '-';
    try { return new Date(s).toLocaleString('tr-TR', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' }); }
    catch { return '-'; }
  };
  const periodLabel = { gunluk: 'Bugün', haftalik: 'Son 7 Gün', aylik: 'Son 30 Gün' }[period];

  return (
    <div>
      {/* Dönem seçici + özet */}
      <div className="flex items-center justify-between flex-wrap gap-2 mb-3">
        <div className="inline-flex rounded-lg bg-white border border-slate-200 p-0.5 shadow-sm">
          {[['gunluk', 'Günlük'], ['haftalik', 'Haftalık'], ['aylik', 'Aylık']].map(([v, l]) => (
            <button
              key={v}
              onClick={() => setPeriod(v)}
              className={`px-3 py-1.5 rounded-md text-xs font-bold transition ${period === v ? 'bg-indigo-600 text-white shadow' : 'text-slate-600 hover:bg-slate-100'}`}
            >
              {l}
            </button>
          ))}
        </div>
        <div className="flex items-center gap-4 text-xs">
          <div className="text-slate-500">
            <span className="font-black text-lg text-indigo-700">{rapor?.toplam_resim || 0}</span> resim
          </div>
          <div className="text-slate-500">
            <span className="font-black text-lg text-slate-800">{rapor?.toplam_kisi || 0}</span> kişi
          </div>
          <span className="text-[11px] text-slate-400">{periodLabel}</span>
        </div>
      </div>

      {yukleniyor ? (
        <div className="flex items-center justify-center py-12 text-slate-500">
          <Loader2 className="w-5 h-5 animate-spin mr-2" /> Rapor hazırlanıyor...
        </div>
      ) : kisiler.length === 0 ? (
        <div className="text-center py-12 bg-white rounded-xl border border-slate-100">
          <Camera className="w-10 h-10 mx-auto text-slate-200 mb-2" />
          <p className="text-slate-500 text-sm font-medium">Bu dönemde resim yükleme kaydı yok</p>
        </div>
      ) : (
        <div className="bg-white rounded-xl border border-slate-100 overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-xs">
              <thead className="bg-slate-50 text-slate-500 text-[10px] uppercase">
                <tr>
                  <th className="text-left px-3 py-2 font-semibold">#</th>
                  <th className="text-left px-3 py-2 font-semibold">Personel</th>
                  <th className="text-left px-3 py-2 font-semibold">Firma</th>
                  <th className="text-right px-3 py-2 font-semibold">Toplam</th>
                  <th className="text-right px-3 py-2 font-semibold">Bugün</th>
                  <th className="text-right px-3 py-2 font-semibold">Aktif Gün</th>
                  <th className="text-right px-3 py-2 font-semibold">Günlük Ort.</th>
                  <th className="text-right px-3 py-2 font-semibold">Son Yükleme</th>
                </tr>
              </thead>
              <tbody>
                {kisiler.map((k, i) => {
                  const az = period === 'gunluk' ? (k.toplam < azEsik) : (k.gunluk_ort < azEsik);
                  return (
                    <tr key={k.user_email || i} className={`border-b border-slate-50 ${az ? 'bg-rose-50/60' : 'hover:bg-slate-50'}`}>
                      <td className="px-3 py-2 text-slate-400 font-bold">
                        {i === 0 ? <Trophy className="w-4 h-4 text-amber-500" /> : i + 1}
                      </td>
                      <td className="px-3 py-2">
                        <div className="font-semibold text-slate-800">{k.user_ad}</div>
                        <div className="text-[10px] text-slate-400">{k.user_email}</div>
                      </td>
                      <td className="px-3 py-2 text-slate-600">{k.firma || '-'}</td>
                      <td className="px-3 py-2 text-right font-black text-indigo-700">{k.toplam}</td>
                      <td className="px-3 py-2 text-right font-semibold text-slate-700">{k.bugun}</td>
                      <td className="px-3 py-2 text-right text-slate-500">{k.aktif_gun}</td>
                      <td className="px-3 py-2 text-right">
                        <span className={`font-bold ${az ? 'text-rose-600' : 'text-emerald-600'}`}>{k.gunluk_ort}</span>
                      </td>
                      <td className="px-3 py-2 text-right text-slate-400 whitespace-nowrap">{fmtDt(k.son_yukleme)}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
          <div className="px-3 py-2 bg-slate-50 border-t border-slate-100 text-[10px] text-slate-400 flex items-center gap-2">
            <span className="inline-block w-3 h-3 rounded bg-rose-50 border border-rose-200"></span>
            {period === 'gunluk' ? `Bugün ${azEsik}'ten az resim çeken` : `Günlük ortalaması ${azEsik}'in altında olan`} personel kırmızı işaretlidir.
          </div>
        </div>
      )}
    </div>
  );
};

const AtadigimGorevlerPanel = ({ data, onDurum }) => {
  const tasks = data?.tasks || [];
  const ozet = data?.ozet || {};
  const durumRenk = (d) => {
    const map = {
      bekliyor: 'bg-amber-100 text-amber-800 border-amber-300',
      yapiliyor: 'bg-blue-100 text-blue-800 border-blue-300',
      devam_ediyor: 'bg-blue-100 text-blue-800 border-blue-300',
      tamamlandi: 'bg-emerald-100 text-emerald-800 border-emerald-300',
      iptal: 'bg-slate-100 text-slate-600 border-slate-300',
    };
    return map[d?.toLowerCase()] || 'bg-slate-100 text-slate-700';
  };
  const tipBadge = (tip) => {
    const map = {
      tahsilat: '💰 Tahsilat',
      arama: '📞 Arama',
      ziyaret: '🚗 Ziyaret',
      gorusme: '💬 Görüşme',
      resim: '🖼️ Görsel',
      genel: '📋 Genel',
    };
    return map[tip] || tip || 'görev';
  };

  return (
    <div className="space-y-3">
      {/* Üst özet + CRM linki */}
      <div className="flex items-center gap-2 mb-1">
        <a
          href="/crm/tasks"
          target="_blank"
          rel="noopener noreferrer"
          className="ml-auto inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-violet-600 hover:bg-violet-700 text-white text-[11px] font-bold transition shadow-sm"
          data-testid="gunaydin-crm-tasks-link"
        >
          🔗 CRM Görev Sayfasında Aç
        </a>
      </div>
      <div className="grid grid-cols-5 gap-2">
        <MiniBadge label="Bekliyor" value={ozet.bekliyor || 0} cls="bg-amber-50 text-amber-800 border-amber-200" />
        <MiniBadge label="Yapılıyor" value={ozet.yapiliyor || 0} cls="bg-blue-50 text-blue-800 border-blue-200" />
        <MiniBadge label="Tamamlandı" value={ozet.tamamlandi || 0} cls="bg-emerald-50 text-emerald-800 border-emerald-200" />
        <MiniBadge label="İptal" value={ozet.iptal || 0} cls="bg-slate-50 text-slate-700 border-slate-200" />
        <MiniBadge label="Geciken" value={ozet.geciken || 0} cls="bg-rose-50 text-rose-800 border-rose-200" />
      </div>

      {tasks.length === 0 ? (
        <div className="bg-white rounded-xl border p-8 text-center text-slate-500">
          Henüz atadığınız görev yok. Vade veya uyarı satırından "Görev Ata" diyerek başlayın.
        </div>
      ) : (
        <div className="bg-white rounded-xl border overflow-hidden">
          <div className="bg-violet-50 px-3 py-2 text-[11px] text-violet-800 border-b border-violet-200">
            Detaylı yönetim için <strong>"CRM Görev Sayfasında Aç"</strong> butonunu kullanabilirsiniz. Burada hızlı durum güncellemesi yapabilirsiniz.
          </div>
          <table className="w-full text-xs">
            <thead className="bg-slate-50 text-slate-600">
              <tr>
                <th className="px-3 py-2 text-left font-semibold">Tip</th>
                <th className="px-3 py-2 text-left font-semibold">Müşteri / Konu</th>
                <th className="px-3 py-2 text-left font-semibold">Atanan</th>
                <th className="px-3 py-2 text-center font-semibold">Durum</th>
                <th className="px-3 py-2 text-center font-semibold">Son Tarih</th>
                <th className="px-3 py-2 text-right font-semibold">Aksiyon</th>
              </tr>
            </thead>
            <tbody className="divide-y">
              {tasks.map((t) => (
                <tr key={t.id} className="hover:bg-slate-50">
                  <td className="px-3 py-2 text-[11px] whitespace-nowrap">{tipBadge(t.tip)}</td>
                  <td className="px-3 py-2">
                    <div className="font-semibold text-slate-800 truncate max-w-[300px]">{t.musteri_bilgileri || t.baslik}</div>
                    <div className="text-[10px] text-slate-500 font-mono">{t.cari_kodu || '—'}{t.firma_adi ? ` • ${t.firma_adi}` : ''}{t.tutar ? ` • ${fmtMoney(t.tutar)} ₺` : ''}</div>
                    {t.sonuc_notu && <div className="text-[10px] text-emerald-700 mt-0.5">✓ {t.sonuc_notu}</div>}
                  </td>
                  <td className="px-3 py-2 text-slate-700">{t.atanan}</td>
                  <td className="px-3 py-2 text-center">
                    <span className={`px-2 py-0.5 rounded-full text-[10px] font-bold border ${durumRenk(t.durum)}`}>
                      {(t.durum || 'bekliyor').toUpperCase()}
                    </span>
                  </td>
                  <td className="px-3 py-2 text-center text-slate-600">
                    {t.bitis_tarih ? new Date(t.bitis_tarih).toLocaleDateString('tr-TR') : '—'}
                  </td>
                  <td className="px-3 py-2 text-right">
                    {(t.durum || 'bekliyor').toLowerCase() !== 'tamamlandi' && (t.durum || '').toLowerCase() !== 'iptal' && (
                      <button
                        onClick={() => onDurum(t)}
                        className="px-2 py-1 rounded bg-orange-600 text-white text-[10px] font-bold hover:bg-orange-700"
                        data-testid={`atadigim-durum-btn-${t.id}`}
                      >
                        Durum Güncelle
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

const MiniBadge = ({ label, value, cls }) => (
  <div className={`rounded-lg border px-2 py-1.5 text-center ${cls}`}>
    <div className="text-[9px] font-semibold uppercase">{label}</div>
    <div className="text-lg font-black">{value}</div>
  </div>
);

// ========= Görev Durum Güncelleme Modal =========
const GorevDurumModal = ({ task, onClose, onSubmit }) => {
  const [durum, setDurum] = useState('tamamlandi');
  const [sonuc, setSonuc] = useState('');
  const [saving, setSaving] = useState(false);

  const handleSubmit = async () => {
    setSaving(true);
    await onSubmit(task.id, durum, sonuc);
    setSaving(false);
  };

  return (
    <div className="fixed inset-0 z-[71] bg-slate-900/70 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-white rounded-xl w-full max-w-md shadow-2xl" onClick={e => e.stopPropagation()}>
        <div className="bg-slate-800 text-white px-4 py-3 rounded-t-xl flex items-center justify-between">
          <h3 className="font-bold text-sm">Görev Durumu Güncelle</h3>
          <button onClick={onClose} className="p-1 hover:bg-white/20 rounded"><X className="w-4 h-4" /></button>
        </div>
        <div className="p-4 space-y-3">
          <div className="bg-slate-50 rounded p-2 text-xs">
            <div className="font-bold text-slate-800">{task.baslik}</div>
            <div className="text-slate-500">Atanan: {task.atanan}</div>
            {task.musteri_bilgileri && <div className="text-slate-500">Müşteri: {task.musteri_bilgileri}</div>}
          </div>
          <div>
            <label className="text-xs font-semibold mb-1 block">Yeni Durum</label>
            <select
              value={durum}
              onChange={(e) => setDurum(e.target.value)}
              className="w-full px-3 py-2 text-xs border rounded bg-white focus:outline-none focus:ring-2 focus:ring-orange-500"
            >
              <option value="yapiliyor">Yapılıyor</option>
              <option value="tamamlandi">Tamamlandı</option>
              <option value="iptal">İptal</option>
            </select>
          </div>
          <div>
            <label className="text-xs font-semibold mb-1 block">Sonuç Notu</label>
            <textarea
              value={sonuc}
              onChange={(e) => setSonuc(e.target.value)}
              rows={3}
              placeholder="Örn: Müşteri 5 gün içinde ödeme yapacağını taahhüt etti..."
              className="w-full px-3 py-2 text-xs border rounded focus:outline-none focus:ring-2 focus:ring-orange-500"
            />
          </div>
        </div>
        <div className="px-4 py-3 bg-slate-50 rounded-b-xl flex justify-end gap-2">
          <button onClick={onClose} className="px-3 py-1.5 text-xs rounded border text-slate-700 hover:bg-slate-100">İptal</button>
          <button
            onClick={handleSubmit}
            disabled={saving}
            className="px-3 py-1.5 text-xs rounded bg-orange-600 text-white font-bold hover:bg-orange-700 disabled:opacity-50 flex items-center gap-1"
          >
            {saving && <Loader2 className="w-3 h-3 animate-spin" />}
            Kaydet
          </button>
        </div>
      </div>
    </div>
  );
};

// ========= Görev Ata Modal =========
const GorevAtaModal = ({ satir, tip = 'tahsilat', personeller, onClose, onSubmit }) => {
  const [atanan, setAtanan] = useState('');
  const [atananId, setAtananId] = useState('');
  const [aciklama, setAciklama] = useState('');
  const [aramaText, setAramaText] = useState('');
  const [seciliTip, setSeciliTip] = useState(tip);
  const [sonTarih, setSonTarih] = useState(() => {
    const d = new Date();
    d.setDate(d.getDate() + 3);
    return d.toISOString().slice(0, 10);
  });
  const [saving, setSaving] = useState(false);

  const tipOptions = [
    { val: 'tahsilat', label: '💰 Tahsilat', desc: 'Bakiye/fatura takibi' },
    { val: 'arama', label: '📞 Arama', desc: 'Müşteri arama / hatırlatma' },
    { val: 'ziyaret', label: '🚗 Ziyaret', desc: 'Saha ziyareti' },
    { val: 'gorusme', label: '💬 Görüşme', desc: 'Yüz yüze veya online toplantı' },
    { val: 'resim', label: '🖼️ Yapılacak (Görsel)', desc: 'Ürün görseli temin / çekim' },
    { val: 'genel', label: '📋 Genel İş', desc: 'Diğer görevler' },
  ];

  const tipBaslik = {
    tahsilat: 'Tahsilat Görevi Ata',
    arama: 'Arama Görevi Ata',
    ziyaret: 'Ziyaret Görevi Ata',
    gorusme: 'Görüşme Görevi Ata',
    resim: 'Yapılacak (Görsel) Görevi Ata',
    genel: 'Görev Ata',
  };

  // Firma + arama metnine göre filtrele
  const filtrelenmis = (() => {
    if (!Array.isArray(personeller)) return [];
    let out = personeller;
    if (satir?.firma) {
      const firma = satir.firma.toUpperCase();
      out = out.filter(p => !p.firma || p.firma.toUpperCase() === firma);
    }
    if (aramaText.trim()) {
      const q = aramaText.toLowerCase();
      out = out.filter(p =>
        (p.ad_soyad || '').toLowerCase().includes(q) ||
        (p.email || '').toLowerCase().includes(q) ||
        (p.role || '').toLowerCase().includes(q)
      );
    }
    return out;
  })();

  const hasPersonel = filtrelenmis.length > 0 || personeller?.length > 0;

  const handlePersonelChange = (e) => {
    const id = e.target.value;
    setAtananId(id);
    if (id === '__custom__') {
      setAtanan('');
      return;
    }
    const p = filtrelenmis.find(x => x.id === id);
    if (p) setAtanan(p.ad_soyad || p.email);
  };

  const handleSubmit = async () => {
    if (!atanan.trim()) { toast.error('Atanan kişi zorunlu'); return; }
    setSaving(true);
    // Listeden seçilen kişinin e-postası da gider: "Bana atanan" e-postayla eşleşir,
    // ad biçimi (ad / ad soyad) farklı olsa da görev kişinin listesinden düşmez
    const secili = atananId && atananId !== '__custom__'
      ? (personeller || []).find(x => x.id === atananId) : null;
    await onSubmit({
      atanan: atanan.trim(),
      atanan_email: secili?.email || '',
      cari_kodu: satir.cari_kodu || '',
      firma: satir.firma || '',
      musteri_ad: satir.musteri_ad || satir.urun_adi || '',
      tutar: satir.tutar || 0,
      aciklama: aciklama.trim() || `${tipBaslik[seciliTip] || 'Görev'} - ${satir.musteri_ad || satir.urun_adi || ''}`,
      son_tarih: sonTarih,
      tip: seciliTip,
      // 🆕 Ürün bilgileri (sku varsa backend products koleksiyonundan zenginleştirir)
      sku: satir.sku || '',
      urun_adi_ham: satir.urun_adi || '',
    });
    setSaving(false);
  };

  return (
    <div className="fixed inset-0 z-[70] bg-slate-900/70 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-white rounded-xl w-full max-w-md shadow-2xl" onClick={e => e.stopPropagation()}>
        <div className="bg-orange-500 text-white px-4 py-3 rounded-t-xl flex items-center justify-between">
          <div className="flex items-center gap-2">
            <UserPlus className="w-4 h-4" />
            <h3 className="font-bold text-sm">{tipBaslik[seciliTip] || 'Görev Ata'}</h3>
          </div>
          <button onClick={onClose} className="p-1 hover:bg-white/20 rounded">
            <X className="w-4 h-4" />
          </button>
        </div>
        <div className="p-4 space-y-3">
          <div className="bg-slate-50 rounded p-2 text-xs">
            <div className="font-bold text-slate-800">{satir.musteri_ad}</div>
            <div className="text-slate-500">{satir.cari_kodu} • {satir.firma}</div>
            <div className="mt-1 text-rose-700 font-bold">
              {fmtMoney(satir.tutar)} TL{satir.gecikme_gun ? ` • ort. ${satir.gecikme_gun} gün gecikme` : ''}
            </div>
          </div>

          <div>
            <label className="text-xs font-semibold text-slate-700 mb-1 block">
              Görev Tipi <span className="text-red-500">*</span>
            </label>
            <select
              value={seciliTip}
              onChange={(e) => setSeciliTip(e.target.value)}
              data-testid="gorev-tip-select"
              className="w-full px-3 py-2 text-xs border border-slate-300 rounded focus:outline-none focus:ring-2 focus:ring-orange-500 bg-white"
            >
              {tipOptions.map(t => (
                <option key={t.val} value={t.val}>{t.label} — {t.desc}</option>
              ))}
            </select>
          </div>

          <div>
            <label className="text-xs font-semibold text-slate-700 mb-1 block">
              Atanacak Personel <span className="text-red-500">*</span>
              {satir.firma && <span className="text-slate-400 font-normal"> — {satir.firma} firması personeli</span>}
            </label>
            <input
              type="text"
              value={aramaText}
              onChange={(e) => setAramaText(e.target.value)}
              placeholder="🔍 Personel ara (ad / e-posta / rol)"
              data-testid="gorev-personel-arama"
              className="w-full mb-2 px-3 py-1.5 text-xs border border-slate-300 rounded bg-white focus:outline-none focus:ring-2 focus:ring-orange-400"
            />
            {hasPersonel ? (
              <select
                value={atananId}
                onChange={handlePersonelChange}
                data-testid="gorev-personel-select"
                size={Math.min(filtrelenmis.length + 1, 6)}
                className="w-full px-3 py-2 text-xs border border-slate-300 rounded focus:outline-none focus:ring-2 focus:ring-orange-500 bg-white"
              >
                <option value="">— Personel seçin —</option>
                {filtrelenmis.length === 0 ? (
                  <option value="" disabled>(Eşleşen personel yok)</option>
                ) : filtrelenmis.map(p => (
                  <option key={p.id} value={p.id}>
                    {p.ad_soyad}{p.role ? ` (${p.role})` : ''}{p.firma ? ` • ${p.firma}` : ''}
                  </option>
                ))}
                <option value="__custom__">➕ Başka birini yaz...</option>
              </select>
            ) : (
              <input
                type="text"
                value={atanan}
                onChange={(e) => setAtanan(e.target.value)}
                placeholder="Personel listesi yüklenemedi, ad-soyad yazın"
                data-testid="gorev-atanan-input"
                className="w-full px-3 py-2 text-xs border border-slate-300 rounded focus:outline-none focus:ring-2 focus:ring-orange-500"
              />
            )}
            {hasPersonel && atananId === '__custom__' && (
              <input
                type="text"
                value={atanan}
                onChange={(e) => setAtanan(e.target.value)}
                placeholder="Ad-Soyad veya e-posta"
                data-testid="gorev-atanan-input-custom"
                className="mt-2 w-full px-3 py-2 text-xs border border-slate-300 rounded focus:outline-none focus:ring-2 focus:ring-orange-500"
              />
            )}
          </div>

          <div>
            <label className="text-xs font-semibold text-slate-700 mb-1 block">Son Tarih</label>
            <input
              type="date"
              value={sonTarih}
              onChange={(e) => setSonTarih(e.target.value)}
              className="w-full px-3 py-2 text-xs border border-slate-300 rounded focus:outline-none focus:ring-2 focus:ring-orange-500"
            />
          </div>

          <div>
            <label className="text-xs font-semibold text-slate-700 mb-1 block">Açıklama / Not</label>
            <textarea
              value={aciklama}
              onChange={(e) => setAciklama(e.target.value)}
              rows={3}
              placeholder="Örn: Müşteri ile bugün arayıp ödeme planı oluştur..."
              className="w-full px-3 py-2 text-xs border border-slate-300 rounded focus:outline-none focus:ring-2 focus:ring-orange-500"
            />
          </div>
        </div>
        <div className="px-4 py-3 bg-slate-50 rounded-b-xl flex justify-end gap-2">
          <button onClick={onClose} className="px-3 py-1.5 text-xs rounded border border-slate-300 text-slate-700 hover:bg-slate-100">
            İptal
          </button>
          <button
            onClick={handleSubmit}
            disabled={saving}
            data-testid="gorev-submit-btn"
            className="px-3 py-1.5 text-xs rounded bg-orange-600 text-white font-bold hover:bg-orange-700 transition disabled:opacity-50 flex items-center gap-1.5"
          >
            {saving ? <Loader2 className="w-3 h-3 animate-spin" /> : <ChevronRight className="w-3 h-3" />}
            Görevi Oluştur
          </button>
        </div>
      </div>
    </div>
  );
};

export default GunaydinDashboard;
